import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Dumbbell, ClipboardList, TrendingUp, Ruler, Users, Building2, CalendarDays, BookOpen } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { FLAGS } from '@/lib/featureFlags';
import { useRouter } from 'expo-router';
import { useSubscription } from '@/hooks/useSubscription';

export type RecordView = 'log' | 'logs' | 'analytics' | 'program';

interface Props {
  activeView: RecordView;
  onChangeView: (view: RecordView) => void;
}

export default function RecordNavHeader({ activeView, onChangeView }: Props) {
  const { theme } = useTheme();
  const router = useRouter();
  const { tier } = useSubscription();

  // Build tab list: core tabs + conditional extras based on tier
  const showProgram = tier !== 'free';
  const tabs: { key: RecordView | 'body' | 'coaching' | 'org'; label: string; Icon: typeof Dumbbell; enabled: boolean }[] = [
    { key: 'log', label: 'Record', Icon: Dumbbell, enabled: true },
    { key: 'logs', label: 'Logs', Icon: ClipboardList, enabled: true },
    { key: 'analytics', label: 'Analytics', Icon: TrendingUp, enabled: true },
    ...(showProgram ? [{ key: 'program' as const, label: 'Program', Icon: BookOpen, enabled: true }] : []),
  ];

  // Only show 4th button for coach/org tiers — free/pro get 3 evenly spaced buttons
  if (tier === 'org_pro') {
    tabs.push({ key: 'org', label: 'Club', Icon: Building2, enabled: true });
  } else if (tier === 'coach_pro') {
    tabs.push({ key: 'coaching', label: 'Coach', Icon: Users, enabled: true });
  }

  return (
    <View
      style={[styles.navHeader, { backgroundColor: theme.colors.background, borderBottomColor: theme.colors.border }]}>
      <View style={styles.navButtons}>
        {tabs.map((tab) => {
          const isSpecial = tab.key === 'body' || tab.key === 'coaching' || tab.key === 'org';
          const isActive = !isSpecial && activeView === tab.key;
          const disabled = isSpecial && !tab.enabled;

          return (
            <TouchableOpacity
              key={tab.key}
              style={[
                styles.navButton,
                {
                  borderBottomColor: isActive ? theme.colors.accent : '#1A1A1A',
                  borderBottomWidth: isActive ? 3 : 1,
                },
              ]}
              onPress={() => {
                if (tab.key === 'coaching') {
                  router.push('/coach-dashboard' as any);
                } else if (tab.key === 'org') {
                  router.push('/org-dashboard' as any);
                } else if (tab.key === 'body') {
                  if (FLAGS.V1_BODY_METRICS_ENABLED) {
                    router.push('/(tabs)/body-metrics');
                  }
                } else {
                  onChangeView(tab.key as RecordView);
                }
              }}
              disabled={disabled}>
              <tab.Icon
                size={20}
                color={
                  disabled
                    ? theme.colors.border
                    : isActive
                      ? theme.colors.accent
                      : '#4A4840'
                }
              />
              <Text
                style={[
                  styles.navButtonText,
                  {
                    color: disabled
                      ? theme.colors.border
                      : isActive
                        ? '#E8E4DB'
                        : '#4A4840',
                  },
                ]}
                numberOfLines={1}>
                {tab.label}
              </Text>
            </TouchableOpacity>
          );
        })}
        {/* Calendar icon */}
        <TouchableOpacity
          style={styles.calendarBtn}
          onPress={() => router.push('/workout-calendar' as any)}
          hitSlop={8}
        >
          <CalendarDays size={20} color={theme.colors.accent} />
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  navHeader: {
    paddingTop: 8,
    paddingBottom: 0,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
  },
  navButtons: {
    flexDirection: 'row',
    gap: 0,
  },
  navButton: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    paddingVertical: 10,
    paddingHorizontal: 2,
    borderRadius: 0,
  },
  navButtonText: {
    fontSize: 11,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 1,
    textTransform: 'uppercase' as const,
  },
  calendarBtn: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 0,
  },
});
